import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserNextLevel } from '../../api/hooks/User/useUserNextLevel'
import { useUserQuestion } from '../../api/hooks/User/useUserQuestion'
import { useUserSaveWrong } from '../../api/hooks/User/useUserSaveWrong'
import { useUserDirectSaveWrong } from '../../api/hooks/User/useUserDirectSaveWrong'
import { useUserDirectChck } from '../../api/hooks/User/useUserDirectChck'
import { useUserUpdateWrong } from '../../api/hooks/User/useUserUpdateWrong'
import {
  useAuthStore,
  useLearningStore,
  useWrongQuizStore,
  useDirectLearningStore,
} from '../../store'
import styles from '../../styles/user/LearningContainer.module.scss'
import LearningProgressTabs from './LearningProgressTabs'
import LearningQuizStep from './LearningQuizStep'
import LearningVideoStep from './LearningVideoStep'
import LearningWrongQuizStep from './LearningWrongQuizStep'
import { useUserGetWrong } from '../../api/hooks/User/useUserGetWrong'
import Loading from '../common/Loading'
import BaseButton from '../common/BaseButton'

const LearningContainer = ({ mode = 'video', userType = 'student' }) => {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [isCompleting, setIsCompleting] = useState(false)
  const [explanationData, setExplanationData] = useState(null)
  const [showExplanation, setShowExplanation] = useState(false)

  const nextLevelMutation = useUserNextLevel()
  const saveWrongMutation = useUserSaveWrong()
  const saveDirectWrongMutation = useUserDirectSaveWrong()
  const updateWrongMutation = useUserUpdateWrong()

  // 사용자 타입별 설정
  const isGeneralUser = userType !== 'student'
  const actualMode = isGeneralUser ? 'quizOnly' : mode
  const { data: quizDirectList, refetch: refetchQuizList } = useUserDirectChck({
    USER_ID: user?.ID || 0,
  })

  // 학생만 오답퀴즈 스텝 체크
  const isQuizOnlyStep = userType === 'student' && user?.STEP % 5 === 0

  const { data: wrongData, isLoading: wrongLoading } = useUserGetWrong({
    ID: user?.ID || 0,
    enabled: isQuizOnlyStep && !!user?.ID,
  })

  const {
    data: questionData,
    isLoading: questionLoading,
    error: questionError,
  } = useUserQuestion({ LEVEL_ID: user?.LEVEL_ID })

  const {
    currentStep,
    initializeLearning,
    getWrongQuestionIds,
    getAnswerSummary,
  } = useLearningStore()

  const { getUserAnswers } = useWrongQuizStore()
  const { getChangedData } = useDirectLearningStore()

  // 로딩 상태 계산
  const isLoading = questionLoading || (userType === 'student' && !user)
  const hasData = questionData?.data && questionData.data.length > 0
  const hasError = questionError || (!hasData && !questionLoading)

  const handleQuizDirectComplete = async () => {
    if (userType === 'general') {
      return
    }

    try {
      const changeData = getChangedData()
      console.log('changeData ? ', changeData)

      if (changeData && changeData.QUESTION_ID) {
        const alreadyAnswered = quizDirectList?.data?.some(
          (q) => q.QUESTION_ID === changeData.QUESTION_ID
        )

        if (alreadyAnswered) {
          console.log('이미 제출한 문제입니다:', changeData.QUESTION_ID)
          useDirectLearningStore.getState().resetQuizData()
          return
        }

        await saveDirectWrongMutation.mutateAsync({
          USER_ID: user.ID,
          QUESTION_ID: changeData.QUESTION_ID,
          ANSWER: changeData.ANSWER,
          IS_CORRECT: changeData.IS_CORRECT,
        })

        await refetchQuizList()
        useDirectLearningStore.getState().resetQuizData()
      }
    } catch (error) {
      console.error('퀴즈 저장 처리 실패:', error)
      useDirectLearningStore.getState().resetQuizData()
    }
  }

  const handleQuizComplete = async () => {
    if (userType === 'general') {
      navigate('/activitySummary', { replace: true })
      return
    }

    try {
      setIsCompleting(true)
      const wrongIds = getWrongQuestionIds()

      if (wrongIds.length > 0) {
        for (const questionId of wrongIds) {
          await saveWrongMutation.mutateAsync({
            ID: user.ID,
            LEVEL_ID: user.LEVEL_ID,
            QUESTION_ID: questionId,
          })
        }
      }


      navigate('/activitySummary', { replace: true })
    } catch (error) {
      console.error('퀴즈 완료 처리 실패:', error)
      setIsCompleting(false)
    }
  }

  const handleWrongQuizComplete = async () => {
    if (!user) return

    try {
      const wrongAnswers = getUserAnswers()

      console.log("wrongAnswers ? ",wrongAnswers)

      if (wrongAnswers.length > 0) {
        const updateData = wrongAnswers.map((answer) => ({
          IS_CORRECT: answer.isCorrect ? 'Y' : 'N',
          USER_ID: user.ID,
          QUESTION_ID: answer.questionId,
        }))
        await updateWrongMutation.mutateAsync(updateData)
      }

       setTimeout(() => {
        navigate('/activitySummaryWrong', { replace: true })
      }, 300)
    } catch (error) {
      console.error('오답 퀴즈 처리 실패:', error)
    }
  }

  // 🔥 학습 초기화 (함수들 뒤에!)
  useEffect(() => {
    console.log(currentStep, 'currentStep')
    if (!isQuizOnlyStep && hasData && questionData?.data) {
      const userId = user?.ID || null
      const actualLevelId = isGeneralUser ? 991111 : user?.LEVEL_ID || 0

      initializeLearning(actualLevelId, userId, questionData.data)

      if (quizDirectList?.data && quizDirectList.data.length > 0) {
        const existingAnswers = quizDirectList.data
          .filter((item) => {
            return questionData.data.some(
              (q) => q.QUESTION_ID === item.QUESTION_ID
            )
          })
          .map((item) => ({
            questionId: item.QUESTION_ID,
            selectedAnswerIndex: item.ANSWER,
            isCorrect: item.IS_CORRECT === 'Y',
            timestamp: new Date(item.REG_DTM || Date.now()),
          }))

        if (existingAnswers.length > 0) {
          console.log('🔄 기존 답변 복원:', existingAnswers.length, '개')
          useLearningStore.getState().setUserAnswers(existingAnswers)

          const firstUnansweredIndex = questionData.data.findIndex(
            (question) => {
              return !existingAnswers.some(
                (answer) => answer.questionId === question.QUESTION_ID
              )
            }
          )

          if (firstUnansweredIndex !== -1) {
            console.log('📍 첫 번째 안 푼 문제로 이동:', firstUnansweredIndex)

            const storeState = useLearningStore.getState()

            if (typeof storeState.completeVideo === 'function') {
              storeState.completeVideo()
            }

            storeState.setCurrentStep('quiz')
            storeState.setCurrentQuestionIndex(firstUnansweredIndex)
          }
        }
      }
    }
  }, [user?.ID, user?.LEVEL_ID, questionData, isQuizOnlyStep, isGeneralUser])

  useEffect(() => {
    const handlePopState = () => {
      navigate('/dashboard/individual', { replace: true })
    }

    window.history.pushState(null, '', window.location.href)
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [navigate])

  // 오답 퀴즈 단계 — 오답 없음
  if (isQuizOnlyStep && !wrongLoading && (!wrongData?.data || wrongData.data.length === 0)) {
    return (
      <div className={styles.loadingWrapper}>
        <img
          src="/assets/confetti-monsters-2.png"
          alt="Congratulations"
          className={styles.errorImage}
        />
        <p className={styles.errorMessage}>
          <span className={styles.errorTitle}>Congratulations!</span>
          <br /> You have no incorrect answers.
        </p>
        <BaseButton
          className={styles.retryButton}
          onClick={handleWrongQuizComplete}
        >
          Next Step&nbsp;&nbsp;→
        </BaseButton>
      </div>
    )
  }

  if (isCompleting) {
    return (
      <div className={styles.loadingWrapper}>
        <Loading />
      </div>
    )
  }

  if (isLoading || (isQuizOnlyStep && wrongLoading)) {
    return (
      <div className={styles.loadingWrapper}>
        <Loading />
      </div>
    )
  }

  if (!isQuizOnlyStep && hasError) {
    return (
      <div className={styles.loadingWrapper}>
        <img
          src="/assets/rabbit-blue-monster-cry.png"
          alt="error image"
          className={styles.errorImage}
        />
        <p className={styles.errorMessage}>Please try again later.</p>
        <button
          onClick={() => window.location.reload()}
          className={styles.retryButton}
        >
          Retry
        </button>
      </div>
    )
  }

  const handleExplanationUpdate = (data) => {
    setExplanationData(data)
    if (data) {
      setShowExplanation(true)
    } else {
      setShowExplanation(false)
    }
  }

  // 해설 패널 렌더 (explanationData 있을 때만 호출됨)
  const renderExplanationPanel = () => (
    <>
      <div className={styles.explanationHeader}>
        <span className={styles.explanationHeaderIcon}>📖</span>
        <span className={styles.explanationHeaderTitle}>Explanation</span>
        <button className={styles.explanationHideBtn} onClick={() => setShowExplanation(false)}>
          Hide ∧
        </button>
      </div>
      <div className={styles.explanationContent}>
        {explanationData.descUrl && (
          <div className={styles.explanationImageCard}>
            <img
              src={explanationData.descUrl}
              alt="Explanation"
              className={styles.explanationDescImage}
            />
          </div>
        )}

        {explanationData.desc && (
          <div className={styles.explanationTextSection}>
            <div className={styles.explanationTextHeader}>
              <span>🔢</span> Step-by-Step Solution
            </div>
            <p className={styles.explanationDescText}>{explanationData.desc}</p>
          </div>
        )}
      </div>
    </>
  )

  const isVideoStep = !isQuizOnlyStep && actualMode === 'video' && currentStep === 'video'
  const explanationOpen = !isVideoStep && !!explanationData && showExplanation

  return (
    <div className={styles.learningFlex}>
      {/* 왼쪽 사이드바 */}
      <aside className={`${styles.sidebarPanel} ${isQuizOnlyStep ? styles.sidebarPanelWrong : ''}`}>
        <LearningProgressTabs
          showVideo={actualMode === 'video'}
          userType={userType}
          quizDirectList={quizDirectList}
        />
      </aside>

      {/* 메인 영역 */}
      <main className={styles.mainPanel}>
        {isQuizOnlyStep ? (
          <LearningWrongQuizStep
            onQuizComplete={handleWrongQuizComplete}
            onExplanationUpdate={handleExplanationUpdate}
          />
        ) : (
          <>
            {actualMode === 'video' && currentStep === 'video' && (
              <LearningVideoStep />
            )}

            {(actualMode === 'quizOnly' ||
              (actualMode === 'video' && currentStep === 'quiz')) && (
              <LearningQuizStep
                onQuizComplete={handleQuizComplete}
                onQuizDirectComplete={handleQuizDirectComplete}
                quizDirectList={quizDirectList}
                onExplanationUpdate={handleExplanationUpdate}
              />
            )}

            {actualMode === 'video' && currentStep === 'completed' && (
              <div className={styles.completedWrapper}>
                <h2><img src="/assets/icons/celebration.png" alt="celebration" style={{ width: 24, height: 24, objectFit: 'contain', verticalAlign: 'middle', marginRight: 6 }} />Learning Complete!</h2>
                <p>You've completed all the questions.</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* 오른쪽 해설 패널 — 슬라이드 인/아웃 */}
      {!isVideoStep && (
        <aside className={`${styles.explanationSlide} ${explanationOpen ? styles.explanationVisible : ''}`}>
          {explanationOpen ? (
            <div className={styles.explanationPanelContainer}>
              {explanationData && renderExplanationPanel()}
            </div>
          ) : (
            explanationData && (
              <button
                className={styles.showExplanationTab}
                onClick={() => setShowExplanation(true)}
              >
                <img src="/assets/icons/hint.png" alt="hint" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                <span>Show</span>
              </button>
            )
          )}
        </aside>
      )}
    </div>
  )
}

export default LearningContainer
